import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { TranslateLoader, TranslationObject } from '@ngx-translate/core';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { environment } from '../../../environments/environment';

const CACHE_PREFIX = 'prizm.wms.mobile.i18n.';
const SOURCE_NAME = 'WMS';

interface AbpUserConfigResponse {
  result?: {
    localization?: {
      values?: Record<string, Record<string, string>>;
    };
  };
}

@Injectable()
export class AbpTranslateLoader implements TranslateLoader {
  private readonly http = inject(HttpClient);

  getTranslation(lang: string): Observable<TranslationObject> {
    return forkJoin({
      bundled: this.bundled(lang),
      remote: this.remote(lang),
    }).pipe(
      map(({ bundled, remote }) => {
        if (remote) {
          this.writeCache(lang, remote);
          return { ...bundled, ...remote } as TranslationObject;
        }
        return { ...bundled, ...(this.readCache(lang) ?? {}) } as TranslationObject;
      }),
    );
  }

  /** Resolves to `null` (never errors) when the backend is unreachable or returns no WMS values. */
  private remote(lang: string): Observable<Record<string, string> | null> {
    const culture = encodeURIComponent(lang);
    const url =
      `${environment.apiUrl ?? ''}/AbpUserConfiguration/GetAll` +
      `?culture=${culture}&ui-culture=${culture}`;

    return this.http.get<AbpUserConfigResponse>(url).pipe(
      map((response) => {
        const dict = response?.result?.localization?.values?.[SOURCE_NAME];
        return dict && Object.keys(dict).length > 0 ? dict : null;
      }),
      catchError(() => of(null)),
    );
  }

  /** Bundled offline snapshot (flat PascalCase keys, mirrors the WMS subset). */
  private bundled(lang: string): Observable<TranslationObject> {
    return this.http
      .get<TranslationObject>(`./i18n/${lang}.json`)
      .pipe(catchError(() => of<TranslationObject>({})));
  }

  private writeCache(lang: string, dict: Record<string, string>): void {
    try {
      globalThis.localStorage?.setItem(CACHE_PREFIX + lang, JSON.stringify(dict));
    } catch {
      // localStorage unavailable or over quota — caching is best-effort.
    }
  }

  private readCache(lang: string): TranslationObject | null {
    try {
      const raw = globalThis.localStorage?.getItem(CACHE_PREFIX + lang);
      return raw ? (JSON.parse(raw) as TranslationObject) : null;
    } catch {
      return null;
    }
  }
}
